<script lang="ts">
	// Manager approval (T20), over Sell and Orders (#379).
	//
	// A manager's yes to one void, one line cancellation or one change of a
	// delivery's driver (UC-66) another cashier asked for. The title names the
	// operation, "Void order C-12", "Cancel 2 × Shawarma on order C-12" or
	// "Change driver of C-12": an approval is for that one only. The shop's
	// active managers are its names, from the till's own list; one picks
	// their name and types their passcode, as on the lock screen, with the
	// same entry, dots and pad. The till's order store sends the digits with
	// the operation and has the server check them; the cashier stays signed
	// in, and the digits are kept nowhere.
	//
	// A wrong passcode, a lock and its countdown show in place under the
	// dots. With no answer from the server the sheet says it needs the
	// internet, and that a manager signed in on this till can approve
	// without it. Close leaves the order as it was. A manager signed in never
	// sees this sheet.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Icon from '../components/Icon.svelte';
	import Keypad from '../components/Keypad.svelte';
	import NameTile from '../components/NameTile.svelte';
	import PasscodeDots from '../components/PasscodeDots.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { message, type Language } from '../i18n/messages';
	import { passcodeMessage, type PasscodeEntry } from '../till/passcode.svelte';
	import type { Cashier, RosterStatus } from '../till/roster.svelte';

	interface Props {
		open: boolean;
		language: Language;
		/** The one operation it approves, in words. */
		title: string;
		/** The till's list, every cashier and manager of its shop. */
		people: Cashier[];
		status: RosterStatus;
		// The entry's own value is the screen's to read; the sheet only types.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		entry: PasscodeEntry<any>;
		onClose: () => void;
	}

	let { open, language, title, people, status, entry, onClose }: Props = $props();

	const managers = $derived(people.filter((p) => p.role === 'manager'));
	const needsInternet = $derived(entry.phase === 'error' && entry.failure === 'internet');
	const checking = $derived(entry.phase === 'checking');

	function close() {
		if (checking) return;
		entry.unpick();
		onClose();
	}
</script>

<Sheet
	{open}
	{title}
	titleTestId="approval-title"
	onClose={close}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="approval-close"
	testId="approval"
>
	<div class="body">
		<div class="who" data-scroll="">
			<p class="lead">{message(language, 'approvalLead')}</p>
			{#if needsInternet}
				<Band tone="flag" testId="approval-internet">{message(language, 'approvalInternet')}</Band>
			{/if}
			{#if status === 'ready' && managers.length === 0}
				<p class="none" data-testid="approval-nobody">{message(language, 'approvalNoManager')}</p>
			{/if}
			{#if entry.picked}
				<div class="picked" data-testid="approval-picked">
					<Icon name="user" size={20} />
					<span class="name">{entry.picked.name}</span>
					<Button
						testId="approval-other"
						tone="text"
						size="sm"
						label={message(language, 'approvalOtherName')}
						disabled={checking}
						onPress={() => entry.unpick()}
					/>
				</div>
			{:else}
				<ul
					class="names"
					role="list"
					aria-label={message(language, 'approvalManagers')}
					data-testid="approval-names"
				>
					{#each managers as manager (manager.id)}
						<NameTile
							testId="approval-name"
							name={manager.name}
							onPick={() => entry.pick(manager)}
						/>
					{/each}
				</ul>
			{/if}
		</div>
		{#if entry.picked}
			<div class="entry">
				<PasscodeDots
					filled={entry.filled}
					state={entry.dots}
					message={needsInternet ? '' : passcodeMessage(entry, language)}
					label={message(language, 'lockPasscode')}
					dotsTestId="approval-dots"
					messageTestId="approval-message"
				/>
				<div class="pad">
					<Keypad
						testId="approval-pad"
						label={message(language, 'lockPasscode')}
						deleteLabel={message(language, 'backspace')}
						fit
						disabled={!entry.open}
						canDelete={entry.canDelete}
						onKey={(key) => entry.type(key)}
						onBackspace={() => entry.backspace()}
					/>
				</div>
			</div>
		{/if}
	</div>
</Sheet>

<style>
	.body {
		flex: 1;
		min-block-size: 0;
		display: flex;
		gap: var(--space-9);
	}

	.who {
		flex: 1 1 280px;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.lead {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	.none {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.names {
		margin: 0;
		padding-block: 0;
		padding-inline-start: 0;
		list-style: none;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--name-min), 1fr));
		gap: var(--touch-gap);
	}

	.picked {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--text-base);
	}

	.name {
		font-weight: 700;
		color: var(--text-strong);
	}

	.entry {
		flex: none;
		inline-size: calc(var(--key-w) * 3 + var(--touch-gap) * 2);
		min-block-size: 0;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-4);
	}

	.pad {
		flex: 1 1 0;
		min-block-size: 0;
		inline-size: 100%;
	}
</style>
